import React, {useState, useEffect, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import axios from 'axios';
import {setLoginRedirect} from '../../actions/loginRedirect.action';

import {
  Wrapper,
  Title,
  Content,
  LinkButton,
  Head,
} from '../../styles/pages/pages';
import {ExtraWrapper} from '../../styles/components/wrapper';
import {
  CodeCard,
  CodeDisplay,
  SubTitle,
  CodeNumber,
  LoadingWrapper,
  ErrorMessage,
  InfoText,
  RefreshButton,
  InstructionsSection,
  InstructionItem,
  InstructionIcon,
  TimerDisplay,
  ButtonWrapper,
  ExpiredMessage,
} from './KioskCodePage.style';
import UnauthInfoPanel from '../../components/UnauthInfoPanel/UnauthInfoPanel';
import Loading from '../../components/Loading/Loading';
import {getBearerHeader} from '../../helpers/api.helper';

const KioskCodePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const auth = useSelector(state => state.auth);
  const authLoading = useSelector(state => state.loading);
  const goToLogin = () => {
    dispatch(setLoginRedirect(location.pathname));
    history.push('/login');
  };

  const [pin, setPin] = useState(null);
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  const timerIntervalRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const fetchThrottleDelay = 2000; // 2 seconds throttle

  const updateTimeLeft = expTimestamp => {
    const now = Math.floor(Date.now() / 1000);
    const secondsLeft = expTimestamp - now;

    if (secondsLeft <= 0) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      setTimeLeft(0);
      setIsExpired(true);
    } else {
      const minutes = Math.floor(secondsLeft / 60);
      const seconds = secondsLeft % 60;
      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      setIsExpired(false);
    }
  };

  const startTimer = expTimestamp => {
    updateTimeLeft(expTimestamp);
    timerIntervalRef.current = setInterval(() => {
      updateTimeLeft(expTimestamp);
    }, 1000);
  };

  const fetchPin = async () => {
    if (!auth || !auth.access_token) {
      return;
    }

    // Throttle: Check if enough time has passed since last fetch
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;

    if (timeSinceLastFetch < fetchThrottleDelay) {
      console.warn(
        `Please wait ${Math.ceil(
          (fetchThrottleDelay - timeSinceLastFetch) / 1000
        )} seconds before generating a new pin`
      );
      return;
    }

    // Update last fetch time
    lastFetchTimeRef.current = now;

    // Clear existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    setLoading(true);
    setError(null);
    setIsExpired(false);

    try {
      const headers = getBearerHeader(auth.access_token);
      const response = await axios.post(
        `/api/account/api/v2/pin-oauth/pin`,
        {},
        {headers}
      );

      if (response.data && response.data.pin && response.data.exp) {
        setPin(response.data.pin);
        setExp(response.data.exp);
        setLoading(false);
        startTimer(response.data.exp);
      } else {
        setError('Unable to retrieve PIN code');
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to fetch PIN code. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth) {
      fetchPin();
    }

    // Cleanup on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [auth]);

  if (!auth) {
    return (
      <UnauthInfoPanel
        callback={goToLogin}
        loading={authLoading && authLoading.auth}
      />
    );
  }

  return (
    <Wrapper>
      <Head>
        <Title>Kiosk Login Pin</Title>
      </Head>
      <Content>
        <CodeCard>
          <SubTitle>Your One-Time Access Pin</SubTitle>
          <InfoText>
            Present this pin at any JJ Foodservice kiosk to login
          </InfoText>
          <CodeDisplay>
            {loading ? (
              <LoadingWrapper>
                <Loading />
              </LoadingWrapper>
            ) : error ? (
              <>
                <ErrorMessage>{error}</ErrorMessage>
                <RefreshButton onClick={fetchPin}>Try Again</RefreshButton>
              </>
            ) : pin ? (
              <>
                {isExpired ? (
                  <>
                    <CodeNumber style={{opacity: 0.4}}>
                      {pin
                        .toString()
                        .split('')
                        .map((digit, index) => (
                          <span key={index}>{digit}</span>
                        ))}
                    </CodeNumber>
                    <ExpiredMessage>Pin Expired</ExpiredMessage>
                    <RefreshButton onClick={fetchPin}>
                      Generate New Pin
                    </RefreshButton>
                  </>
                ) : (
                  <>
                    <CodeNumber>
                      {pin
                        .toString()
                        .split('')
                        .map((digit, index) => (
                          <span key={index}>{digit}</span>
                        ))}
                    </CodeNumber>
                    <ButtonWrapper>
                      {timeLeft && (
                        <TimerDisplay
                          $warning={timeLeft && timeLeft.startsWith('0:')}
                        >
                          Expires in: {timeLeft}
                        </TimerDisplay>
                      )}
                      <RefreshButton onClick={fetchPin}>
                        Generate New Pin
                      </RefreshButton>
                    </ButtonWrapper>
                  </>
                )}
              </>
            ) : (
              <>
                <ErrorMessage>No pin available</ErrorMessage>
                <RefreshButton onClick={fetchPin}>Generate Pin</RefreshButton>
              </>
            )}
          </CodeDisplay>
        </CodeCard>
        <InstructionsSection>
          <SubTitle>How to Use</SubTitle>
          <InstructionItem>
            <InstructionIcon>1</InstructionIcon>
            <div>
              <strong>Find a kiosk</strong>
              <p>
                Locate a JJ Foodservice self-service kiosk at your nearest
                branch
              </p>
            </div>
          </InstructionItem>
          <InstructionItem>
            <InstructionIcon>2</InstructionIcon>
            <div>
              <strong>Tap a login button</strong>
              <p>Tap Login with your account on kiosk</p>
            </div>
          </InstructionItem>
          <InstructionItem>
            <InstructionIcon>3</InstructionIcon>
            <div>
              <strong>Enter your pin</strong>
              <p>Type the 6-digit pin displayed above into the kiosk</p>
            </div>
          </InstructionItem>
          <InstructionItem>
            <InstructionIcon>4</InstructionIcon>
            <div>
              <strong>Start shopping</strong>
              <p>Your account will be logged in and ready to use</p>
            </div>
          </InstructionItem>
        </InstructionsSection>
      </Content>
      <ExtraWrapper>
        <LinkButton to="/">Continue shopping</LinkButton>
      </ExtraWrapper>
    </Wrapper>
  );
};

export default KioskCodePage;
